import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Section, Button } from '../components/ui';
import SEO from '../components/SEO';

const PageHeader = styled.div`
  text-align: center; padding: 4rem 0;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
`;

const PageTitle = styled.h1`margin-bottom: 1rem;`;

const PageDesc = styled.p`
  color: #6b7280; font-size: 1.25rem; max-width: 720px;
  margin: 0 auto; line-height: 1.625;
`;

const Content = styled.div`
  max-width: 720px; margin: 0 auto;
  font-size: 1.125rem; line-height: 1.625; color: #374151;
  
  h2 { margin-top: 3rem; margin-bottom: 1rem; }
  h3 { margin-top: 2rem; margin-bottom: 0.75rem; }
  p { margin-bottom: 1.5rem; }
  ul { margin-bottom: 1.5rem; padding-left: 1.5rem; }
  li { margin-bottom: 0.5rem; }
`;

const Highlight = styled.span`color: #eab308; font-weight: 600;`;

const MathBox = styled.div`
  background: #0f0f0f; color: white; padding: 2rem;
  border-radius: 1rem; text-align: center; margin: 2rem 0;
`;

const Equation = styled.div`
  font-size: 2.25rem; font-weight: 700; font-family: monospace;
  margin-bottom: 1rem;
  span { color: #eab308; }
`;

const MathDesc = styled.p`color: #6b7280; font-size: 1rem; margin: 0;`;

const SeasonGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;
  margin: 2rem 0;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const SeasonCard = styled.div`
  background: #f9fafb; padding: 1.5rem; border-radius: 0.75rem;
`;

const SeasonTitle = styled.h4`margin-bottom: 0.5rem; font-size: 1.125rem;`;

const SeasonDesc = styled.p`color: #6b7280; margin: 0; font-size: 1rem;`;

const CTABox = styled.div`
  background: #fef3c7; padding: 3rem; border-radius: 1rem;
  text-align: center; margin-top: 3rem;
  h3 { margin-bottom: 1rem; }
  p { color: #6b7280; margin-bottom: 1.5rem; }
`;

const About = () => (
  <>
    <SEO title="About" description="Learn about The New Calendar - the first timekeeping system built with math, science, and logic." url="https://thenewcalendar.com/about" />
    
    <PageHeader>
      <Container>
        <PageTitle>The Discovery</PageTitle>
        <PageDesc>For the first time in history, a calendar system designed around how time actually works—not politics, religion, or ancient tradition.</PageDesc>
      </Container>
    </PageHeader>

    <Section>
      <Container>
        <Content>
          <h2>The Problem</h2>
          <p>Have you ever wondered why February has 28 days, while January has 31? Why some months are longer than others? Why an octopus has 8 arms, an octogon has 8 sides, but "October" is the 10th month?</p>
          <p>The Gregorian Calendar we use today is over 400 years old, built on a Roman system that's nearly 3,000 years old. It was designed gradually by politicians and religious leaders—not scientists or mathematicians.</p>
          <p><Highlight>Until now, time was the only measurement system never updated after the Scientific Revolution.</Highlight></p>

          <h2>The Discovery</h2>
          <p>In 2017, a team of researchers asked a simple question: What if we designed a calendar using math, science, and logic?</p>
          <p>They started with the most basic fact: Earth takes 365 days to orbit the sun. So what's the most logical way to divide 365?</p>

          <MathBox>
            <Equation>365 = <span>5</span> × <span>73</span></Equation>
            <MathDesc>The only pair of factors for 365 are 5 and 73—both prime numbers.</MathDesc>
          </MathBox>

          <p>This mathematical fact reveals something profound: <Highlight>the year naturally divides into 5 seasons of exactly 73 days each.</Highlight></p>

          <h2>The Five Seasons</h2>
          <p>The New Calendar anchors to the Winter Solstice as Day 1. From there, the five 73-day seasons unfold:</p>

          <SeasonGrid>
            <SeasonCard>
              <SeasonTitle>❄️ Winter</SeasonTitle>
              <SeasonDesc>Days 1-73: The cold, quiet period after the solstice</SeasonDesc>
            </SeasonCard>
            <SeasonCard>
              <SeasonTitle>🌱 Spring</SeasonTitle>
              <SeasonDesc>Days 74-146: The awakening, early growth period</SeasonDesc>
            </SeasonCard>
            <SeasonCard>
              <SeasonTitle>☀️ Summer</SeasonTitle>
              <SeasonDesc>Days 147-219: Peak summer, centered on the solstice</SeasonDesc>
            </SeasonCard>
            <SeasonCard>
              <SeasonTitle>🌽 Autumn</SeasonTitle>
              <SeasonDesc>Days 220-292: The harvest and transition period, inverse Spring</SeasonDesc>
            </SeasonCard>
          </SeasonGrid>
          <p style={{ textAlign: 'center', fontWeight: 600 }}>+ <span style={{ color: '#eab308' }}>🍂 Fall</span> — Days 293-365: Leaves turn brown and fall on the ground.</p>
          <h2>Why It Matters</h2>
          <ul>
            <li><strong>Perpetual:</strong> Days and dates never shift. Buy once, use forever.</li>
            <li><strong>Natural:</strong> Aligned with solstices and natural patterns.</li>
            <li><strong>Uniform:</strong> Every season is exactly 73 days.</li>
            <li><strong>Logical:</strong> Built on mathematical truth, not ancient politics.</li>
          </ul>

          <CTABox>
            <h3>Ready to rethink time?</h3>
            <p>Join thousands who have discovered a better way to organize their year.</p>
            <Button as={Link} to="/shop" size="lg">Shop The Calendar</Button>
          </CTABox>
        </Content>
      </Container>
    </Section>
  </>
);

export default About;
